import connection_production from "../config/connection-production.json";
import connection_local from "../config/connection-local.json";
import connection_development from "../config/connection-development.json";

// The RECEIVE half of admin impersonation. app-web never mints a hand-off — only admin-web can, and only an
// ADMIN there — so this file deliberately carries no `canImpersonate*` gate and no URL builder. Its whole job
// is to read the code out of the URL and to know where "Yöneticiye dön" goes.
//
// ⚠️ SECURITY — what arrives in the URL is a SHORT-LIVED, SINGLE-USE CODE, never a token. It is minted
// against the admin's own profile-scoped token and burned by the first caller to redeem it, so a leaked link
// is worthless moments later. That is a materially different risk class from this platform's other hand-offs
// (`ExternalClientHandoffUtilization`), which accept a full access + refresh token pair replayable for a
// week. See admin-web's ImpersonationHandoffUtilization for the full reasoning and
// `plans/academy-handoff-short-lived-code.md`, which specifies this shape.
//
// It rides in the FRAGMENT, not the query string. A fragment is the one part of a URL a browser does not send
// to a server: no access log on this host, no TLS-terminating proxy / CDN / WAF log on the way in, no
// `Referer`. Do not "tidy" this into a query string.

const connection =
  process.env.REACT_APP_NODE_ENV === "local"
    ? connection_local
    : process.env.REACT_APP_NODE_ENV === "development"
      ? connection_development
      : process.env.REACT_APP_NODE_ENV === "production"
        ? connection_production
        : connection_local;

export type ImpersonationHandoffCode = {
  code: string | null;
  /** Always `"fragment"` when a code was found — there is deliberately no query fallback. */
  source: "fragment" | null;
};

/**
 * Reads the hand-off code out of the URL. **Fragment only — no query-string fallback.**
 *
 * `ExternalClientHandoffUtilization` accepts `?accessToken=…` as a one-release shim because an existing
 * partner contract produced that shape. This route is new and has no legacy caller, so accepting `?code=`
 * would manufacture from nothing the exact access-log exposure this design exists to remove.
 *
 * @param hash `location.hash`, with or without its leading `#`
 */
export const readImpersonationHandoffCode = (
  hash?: string | null,
): ImpersonationHandoffCode => {
  const fragmentParams = new URLSearchParams((hash ?? "").replace(/^#/, ""));
  const code = fragmentParams.get("code");
  if (code) {
    return { code, source: "fragment" };
  }
  return { code: null, source: null };
};

/**
 * Where "Yöneticiye dön" goes.
 *
 * A learner impersonation lands on app-web, a DIFFERENT origin from admin-web, so the admin's own session was
 * never touched — there is nothing to restore here, and returning is a plain navigation back to a session
 * that is still signed in. That is why app-web carries no actor stash at all.
 */
export const retrieveAdminAppUrl = (): string | null =>
  connection.admin?.url ?? null;
